import * as Sentry from "@sentry/nextjs";
import type { LogContext, LogLevel, Logger } from "./types";
import type { SeverityLevel } from "@sentry/nextjs";

const parseBoolean = (value?: string): boolean | undefined => {
  if (value === undefined) return undefined;
  if (value === "true" || value === "1") return true;
  if (value === "false" || value === "0") return false;
  return undefined;
};

const parseComponentList = (value?: string) => {
  if (!value) return new Set<string>();
  return new Set(
    value
      .split(",")
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean)
  );
};

const componentAllowList = parseComponentList(
  process.env.NEXT_PUBLIC_LOG_COMPONENT_ALLOW || process.env.LOG_COMPONENT_ALLOW
);
const componentDenyList = parseComponentList(
  process.env.NEXT_PUBLIC_LOG_COMPONENT_DENY || process.env.LOG_COMPONENT_DENY
);
const warnedInvalidComponents = new Set<string>();

const normalizeComponent = (value?: unknown) => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (trimmed.length > 64 || !/^[a-z0-9_-]+$/i.test(trimmed)) {
    if (!warnedInvalidComponents.has(trimmed)) {
      warnedInvalidComponents.add(trimmed);
      console.warn("Invalid log component name; using 'unknown'.", {
        component: trimmed,
      });
    }
    return undefined;
  }
  return trimmed.toLowerCase();
};

const mapLogLevelToSeverity = (level: LogLevel): SeverityLevel => {
  switch (level) {
    case "debug":
      return "debug";
    case "info":
      return "info";
    case "warn":
      return "warning";
    case "error":
      return "error";
    case "fatal":
      return "fatal";
  }
};

const shouldLog = (level: LogLevel, component?: string) => {
  if (level === "error" || level === "fatal") return true;
  if (!component) return true;
  if (componentDenyList.has(component)) return false;
  if (componentAllowList.size > 0 && !componentAllowList.has(component)) {
    return false;
  }
  return true;
};

const resolveSentryEnabled = (dsn?: string) => {
  const explicit =
    parseBoolean(process.env.NEXT_PUBLIC_ENABLE_GLITCHTIP) ??
    parseBoolean(process.env.NEXT_PUBLIC_SENTRY_ENABLED);
  if (explicit !== undefined) return explicit;
  return Boolean(dsn);
};

const normalizeError = (error: Error | string) => {
  if (error instanceof Error) {
    return { err: error, message: error.message };
  }
  return { err: undefined, message: error };
};

const mergeContext = (base?: LogContext, extra?: LogContext) => ({
  ...(base || {}),
  ...(extra || {}),
});

const createLoggerInstance = (baseContext?: LogContext): Logger => {
  const dsn = process.env.NEXT_PUBLIC_GLITCHTIP_DSN || process.env.NEXT_PUBLIC_SENTRY_DSN;
  const sentryEnabled = resolveSentryEnabled(dsn);

  const captureException = (error: Error, context?: LogContext) => {
    if (!sentryEnabled) return "";
    return Sentry.captureException(error, {
      extra: mergeContext(baseContext, context),
    }) as string;
  };

  const captureMessage = (
    message: string,
    level: LogLevel = "info",
    context?: LogContext
  ) => {
    if (!sentryEnabled) return "";
    return Sentry.captureMessage(message, {
      level: mapLogLevelToSeverity(level),
      extra: mergeContext(baseContext, context),
    }) as string;
  };

  const logToConsole = (level: LogLevel, message: string, context?: LogContext) => {
    if (process.env.NODE_ENV === "production") return;
    const payload = mergeContext(baseContext, context);
    const output = Object.keys(payload).length ? payload : undefined;
    switch (level) {
      case "debug":
        console.debug(message, output);
        break;
      case "info":
        console.info(message, output);
        break;
      case "warn":
        console.warn(message, output);
        break;
      case "error":
      case "fatal":
        console.error(message, output);
        break;
      default:
        console.log(message, output);
    }
  };

  const log = (level: LogLevel, message: string, context?: LogContext, error?: Error) => {
    const merged = mergeContext(baseContext, context);
    const normalizedComponent = normalizeComponent(merged.component);
    const hasComponent = merged.component !== undefined;
    const componentForFilter = hasComponent
      ? normalizedComponent || "unknown"
      : undefined;
    if (hasComponent) {
      merged.component = componentForFilter;
    }
    if (shouldLog(level, componentForFilter)) {
      logToConsole(level, message, merged);
    }
    if (error) {
      captureException(error, merged);
    }
  };

  return {
    debug(message, context) {
      log("debug", message, context);
    },
    info(message, context) {
      log("info", message, context);
    },
    warn(message, context) {
      log("warn", message, context);
    },
    error(error, context) {
      const normalized = normalizeError(error);
      log("error", normalized.message, context, normalized.err);
      if (normalized.err) {
        captureException(normalized.err, context);
      } else {
        captureMessage(normalized.message, "error", context);
      }
    },
    fatal(error, context) {
      const normalized = normalizeError(error);
      log("fatal", normalized.message, context, normalized.err);
      if (normalized.err) {
        captureException(normalized.err, context);
      } else {
        captureMessage(normalized.message, "fatal", context);
      }
    },
    captureException,
    captureMessage,
    setUser(user) {
      if (!sentryEnabled) return;
      Sentry.setUser(user);
    },
    setContext(key, context) {
      if (!sentryEnabled) return;
      Sentry.setContext(key, context);
    },
    addBreadcrumb(breadcrumb) {
      if (!sentryEnabled) return;
      const mappedBreadcrumb = {
        ...breadcrumb,
        level: breadcrumb.level ? mapLogLevelToSeverity(breadcrumb.level) : undefined,
      };
      Sentry.addBreadcrumb(mappedBreadcrumb);
    },
    child(context) {
      return createLoggerInstance(mergeContext(baseContext, context));
    },
  };
};

export const logger: Logger = createLoggerInstance({});
